/**
 * useRevokeInvitation — TanStack mutation over
 * `POST /organizations/:slug/invitations/:id/revoke`.
 *
 * Body: (none). Response: 204 No Content.
 *
 * The BE use case is idempotent on already-revoked (no error thrown),
 * so the FE only treats already-accepted as a true 422 conflict.
 *
 * Error envelope (tagged union):
 *   - 'unprocessable' — 422; carries `reason` (e.g. 'already_accepted').
 *   - 'forbidden'    — 403 (missing identity.invitations.revoke).
 *   - 'notFound'     — 404 (invitation or organization not found).
 *   - 'error'        — anything else.
 *
 * Invalidates the shared invitations list + the audit feed on success.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { buildInvitationsListQueryKey } from './use-create-invitation.js';

export interface RevokeInvitationInput {
  readonly invitationId: string;
}

export type RevokeInvitationError =
  | { readonly kind: 'unprocessable'; readonly reason: string }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'notFound' }
  | { readonly kind: 'error' };

export interface UseRevokeInvitationResult {
  readonly mutateAsync: (input: RevokeInvitationInput) => Promise<null>;
  readonly isPending: boolean;
}

export function useRevokeInvitation(): UseRevokeInvitationResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;
  const queryClient = useQueryClient();

  // Note: `useMutation` does not accept `void` as its first generic
  // because lint forbids `void` outside return types. Use `null` and
  // resolve with it on success — call sites await without consuming
  // the value (see InvitationsListPage onRevoke).
  const mutation = useMutation<
    null,
    RevokeInvitationError,
    RevokeInvitationInput
  >({
    mutationFn: async (input) => {
      if (orgSlug === null) {
        throw { kind: 'error' } satisfies RevokeInvitationError;
      }
      const { response, data, error } = await apiClient.POST(
        '/organizations/{slug}/invitations/{id}/revoke',
        {
          params: { path: { slug: orgSlug, id: input.invitationId } },
        },
      );
      if (response.status === 422) {
        const body = (error ?? data) as { message?: unknown } | undefined;
        const reason =
          typeof body?.message === 'string' ? body.message : 'unprocessable';
        throw {
          kind: 'unprocessable',
          reason,
        } satisfies RevokeInvitationError;
      }
      if (response.status === 403) {
        throw { kind: 'forbidden' } satisfies RevokeInvitationError;
      }
      if (response.status === 404) {
        throw { kind: 'notFound' } satisfies RevokeInvitationError;
      }
      if (!response.ok) {
        throw { kind: 'error' } satisfies RevokeInvitationError;
      }
      return null;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: buildInvitationsListQueryKey(orgSlug),
      });
      await queryClient.invalidateQueries({
        predicate: (q) => {
          const k = q.queryKey;
          return (
            Array.isArray(k) &&
            k[0] === 'identity' &&
            k[2] === orgSlug &&
            k.includes('audit')
          );
        },
      });
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}
